import { useState } from 'react'
import toast from 'react-hot-toast'
import {
    RiArrowDownSLine,
    RiArrowUpSLine,
    RiAddLine,
    RiDeleteBinLine,
    RiEditLine,
    RiSaveLine,
    RiCloseLine,
} from 'react-icons/ri'
import {
    useAdminPrices,
    useAdminMaterials,
    useAdminSizes,
    useCreatePrice,
    useUpdatePrice,
    useDeletePrice,
    useCreateVolumeTier,
    useUpdateVolumeTier,
    useDeleteVolumeTier,
} from '@/api/useAdminCatalog'
import { Spinner } from '@/components/ui'
import type { PriceConfig } from '@/api/usePrices'
import styles from './AdminPrices.module.css'

interface PriceForm {
    material: number
    size: number
    price_per_millar: string
    min_millares: number
    is_active: boolean
}

const EMPTY_FORM: PriceForm = {
    material: 0,
    size: 0,
    price_per_millar: '',
    min_millares: 1,
    is_active: true,
}

interface TierForm {
    min_millares: string
    price_per_millar: string
}

const EMPTY_TIER_FORM: TierForm = { min_millares: '', price_per_millar: '' }

export function AdminPrices() {
    const { data: prices, isLoading } = useAdminPrices()
    const { data: materials } = useAdminMaterials()
    const { data: sizes } = useAdminSizes()
    const createPrice = useCreatePrice()
    const updatePrice = useUpdatePrice()
    const deletePrice = useDeletePrice()

    const [showAddRow, setShowAddRow] = useState(false)
    const [addForm, setAddForm] = useState<PriceForm>(EMPTY_FORM)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editForm, setEditForm] = useState<PriceForm>(EMPTY_FORM)

    const [expandedPriceId, setExpandedPriceId] = useState<number | null>(null)
    const [showAddTier, setShowAddTier] = useState(false)
    const [addTierForm, setAddTierForm] = useState<TierForm>(EMPTY_TIER_FORM)
    const [editingTierId, setEditingTierId] = useState<number | null>(null)
    const [editTierForm, setEditTierForm] = useState<TierForm>(EMPTY_TIER_FORM)

    const createTier = useCreateVolumeTier()
    const updateTier = useUpdateVolumeTier()
    const deleteTier = useDeleteVolumeTier()

    const handleAddSave = () => {
        if (!addForm.material || !addForm.size) {
            toast.error('Selecciona material y tamaño')
            return
        }
        createPrice.mutate(addForm, {
            onSuccess: () => {
                toast.success('Precio creado')
                setShowAddRow(false)
                setAddForm(EMPTY_FORM)
            },
            onError: () => toast.error('Error al crear precio'),
        })
    }

    const handleEditStart = (price: PriceConfig) => {
        setEditingId(price.id)
        setEditForm({
            material: price.material,
            size: price.size,
            price_per_millar: price.price_per_millar,
            min_millares: price.min_millares,
            is_active: price.is_active,
        })
    }

    const handleEditSave = (id: number) => {
        updatePrice.mutate(
            { id, ...editForm },
            {
                onSuccess: () => {
                    toast.success('Precio actualizado')
                    setEditingId(null)
                },
                onError: () => toast.error('Error al actualizar precio'),
            }
        )
    }

    const handleDelete = (id: number) => {
        if (!window.confirm('¿Eliminar esta configuración de precio?')) return
        deletePrice.mutate(id, {
            onSuccess: () => toast.success('Precio eliminado'),
            onError: () => toast.error('Error al eliminar precio'),
        })
    }

    const handleToggleExpand = (priceId: number) => {
        if (expandedPriceId === priceId) {
            setExpandedPriceId(null)
            setShowAddTier(false)
            setEditingTierId(null)
        } else {
            setExpandedPriceId(priceId)
            setShowAddTier(false)
            setEditingTierId(null)
            setAddTierForm(EMPTY_TIER_FORM)
        }
    }

    const materialOptions = materials ?? []
    const sizeOptions = sizes ?? []

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Matriz de Precios</h1>
                <button
                    className={styles.addButton}
                    onClick={() => {
                        setShowAddRow(true)
                        setAddForm(EMPTY_FORM)
                        setEditingId(null)
                    }}
                >
                    + Agregar Precio
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loading}>
                    <Spinner size="lg" />
                </div>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Material</th>
                            <th>Tamaño</th>
                            <th>Precio/Millar (S/)</th>
                            <th>Mín. Millares</th>
                            <th>Activo</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showAddRow && (
                            <tr className={styles.formRow}>
                                <td>
                                    <select
                                        className={styles.formSelect}
                                        value={addForm.material}
                                        onChange={(e) => setAddForm({ ...addForm, material: Number(e.target.value) })}
                                    >
                                        <option value={0}>-- Material --</option>
                                        {materialOptions.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <select
                                        className={styles.formSelect}
                                        value={addForm.size}
                                        onChange={(e) => setAddForm({ ...addForm, size: Number(e.target.value) })}
                                    >
                                        <option value={0}>-- Tamaño --</option>
                                        {sizeOptions.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={styles.formInput}
                                        value={addForm.price_per_millar}
                                        onChange={(e) => setAddForm({ ...addForm, price_per_millar: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0.5"
                                        className={styles.formInput}
                                        value={addForm.min_millares}
                                        onChange={(e) => setAddForm({ ...addForm, min_millares: Number(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className={styles.formCheckbox}
                                        checked={addForm.is_active}
                                        onChange={(e) => setAddForm({ ...addForm, is_active: e.target.checked })}
                                    />
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        <button
                                            className={styles.saveBtn}
                                            onClick={handleAddSave}
                                            disabled={createPrice.isPending}
                                        >
                                            Guardar
                                        </button>
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={() => setShowAddRow(false)}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!prices || prices.length === 0 ? (
                            <tr>
                                <td colSpan={6} className={styles.empty}>
                                    No hay precios configurados
                                </td>
                            </tr>
                        ) : (
                            prices.map((price) =>
                                editingId === price.id ? (
                                    <tr key={price.id} className={styles.formRow}>
                                        <td>
                                            <select
                                                className={styles.formSelect}
                                                value={editForm.material}
                                                onChange={(e) => setEditForm({ ...editForm, material: Number(e.target.value) })}
                                            >
                                                {materialOptions.map((m) => (
                                                    <option key={m.id} value={m.id}>{m.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                className={styles.formSelect}
                                                value={editForm.size}
                                                onChange={(e) => setEditForm({ ...editForm, size: Number(e.target.value) })}
                                            >
                                                {sizeOptions.map((s) => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={styles.formInput}
                                                value={editForm.price_per_millar}
                                                onChange={(e) => setEditForm({ ...editForm, price_per_millar: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.5"
                                                min="0.5"
                                                className={styles.formInput}
                                                value={editForm.min_millares}
                                                onChange={(e) => setEditForm({ ...editForm, min_millares: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className={styles.formCheckbox}
                                                checked={editForm.is_active}
                                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                            />
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.saveBtn}
                                                    onClick={() => handleEditSave(price.id)}
                                                    disabled={updatePrice.isPending}
                                                >
                                                    Guardar
                                                </button>
                                                <button
                                                    className={styles.cancelBtn}
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        <tr key={price.id}>
                                            <td>{price.material_name}</td>
                                            <td>{price.size_name}</td>
                                            <td>
                                                S/ {price.price_per_millar}
                                                {price.tiers && price.tiers.length > 0 && (
                                                    <span className={styles.tierBadge}>{price.tiers.length} niveles</span>
                                                )}
                                            </td>
                                            <td>{price.min_millares}</td>
                                            <td>
                                                <span className={`${styles.badge} ${price.is_active ? styles.badgeActive : styles.badgeInactive}`}>
                                                    {price.is_active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actions}>
                                                    <button
                                                        className={styles.tierToggleBtn}
                                                        onClick={() => handleToggleExpand(price.id)}
                                                        title="Descuentos por volumen"
                                                    >
                                                        {expandedPriceId === price.id ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
                                                    </button>
                                                    <button
                                                        className={styles.editBtn}
                                                        onClick={() => handleEditStart(price)}
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        className={styles.deleteBtn}
                                                        onClick={() => handleDelete(price.id)}
                                                        disabled={deletePrice.isPending}
                                                    >
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedPriceId === price.id && (
                                            <tr key={`tiers-${price.id}`}>
                                                <td colSpan={6} className={styles.tierContainer}>
                                                    <div className={styles.tierSection}>
                                                        <div className={styles.tierHeader}>
                                                            <span className={styles.tierTitle}>Descuentos por volumen</span>
                                                            <button
                                                                className={styles.addTierBtn}
                                                                onClick={() => {
                                                                    setShowAddTier(true)
                                                                    setAddTierForm(EMPTY_TIER_FORM)
                                                                }}
                                                            >
                                                                <RiAddLine /> Agregar nivel
                                                            </button>
                                                        </div>

                                                        <div className={styles.tierNote}>
                                                            Precio base: S/ {price.price_per_millar}/millar (desde {price.min_millares} millar)
                                                        </div>

                                                        {showAddTier && (
                                                            <div className={styles.tierFormRow}>
                                                                <input
                                                                    type="number"
                                                                    step="0.5"
                                                                    min="0.5"
                                                                    className={styles.tierInput}
                                                                    placeholder="Desde (millares)"
                                                                    value={addTierForm.min_millares}
                                                                    onChange={(e) => setAddTierForm({ ...addTierForm, min_millares: e.target.value })}
                                                                />
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className={styles.tierInput}
                                                                    placeholder="Precio/millar"
                                                                    value={addTierForm.price_per_millar}
                                                                    onChange={(e) => setAddTierForm({ ...addTierForm, price_per_millar: e.target.value })}
                                                                />
                                                                <button
                                                                    className={styles.tierSaveBtn}
                                                                    onClick={() => {
                                                                        createTier.mutate(
                                                                            {
                                                                                price_config: price.id,
                                                                                min_millares: Number(addTierForm.min_millares),
                                                                                price_per_millar: addTierForm.price_per_millar,
                                                                            },
                                                                            {
                                                                                onSuccess: () => {
                                                                                    toast.success('Nivel creado')
                                                                                    setShowAddTier(false)
                                                                                    setAddTierForm(EMPTY_TIER_FORM)
                                                                                },
                                                                                onError: () => toast.error('Error al crear nivel'),
                                                                            }
                                                                        )
                                                                    }}
                                                                    disabled={createTier.isPending}
                                                                >
                                                                    <RiSaveLine />
                                                                </button>
                                                                <button
                                                                    className={styles.tierCancelBtn}
                                                                    onClick={() => setShowAddTier(false)}
                                                                >
                                                                    <RiCloseLine />
                                                                </button>
                                                            </div>
                                                        )}

                                                        {price.tiers && price.tiers.length > 0 ? (
                                                            <div className={styles.tierList}>
                                                                {price.tiers.map((tier) =>
                                                                    editingTierId === tier.id ? (
                                                                        <div key={tier.id} className={styles.tierFormRow}>
                                                                            <input
                                                                                type="number"
                                                                                step="0.5"
                                                                                min="0.5"
                                                                                className={styles.tierInput}
                                                                                value={editTierForm.min_millares}
                                                                                onChange={(e) => setEditTierForm({ ...editTierForm, min_millares: e.target.value })}
                                                                            />
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                className={styles.tierInput}
                                                                                value={editTierForm.price_per_millar}
                                                                                onChange={(e) => setEditTierForm({ ...editTierForm, price_per_millar: e.target.value })}
                                                                            />
                                                                            <button
                                                                                className={styles.tierSaveBtn}
                                                                                onClick={() => {
                                                                                    updateTier.mutate(
                                                                                        {
                                                                                            id: tier.id,
                                                                                            price_config: price.id,
                                                                                            min_millares: Number(editTierForm.min_millares),
                                                                                            price_per_millar: editTierForm.price_per_millar,
                                                                                        },
                                                                                        {
                                                                                            onSuccess: () => {
                                                                                                toast.success('Nivel actualizado')
                                                                                                setEditingTierId(null)
                                                                                            },
                                                                                            onError: () => toast.error('Error al actualizar'),
                                                                                        }
                                                                                    )
                                                                                }}
                                                                                disabled={updateTier.isPending}
                                                                            >
                                                                                <RiSaveLine />
                                                                            </button>
                                                                            <button
                                                                                className={styles.tierCancelBtn}
                                                                                onClick={() => setEditingTierId(null)}
                                                                            >
                                                                                <RiCloseLine />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div key={tier.id} className={styles.tierRow}>
                                                                            <span className={styles.tierQty}>{tier.min_millares}+ millares</span>
                                                                            <span className={styles.tierPrice}>S/ {tier.price_per_millar}/millar</span>
                                                                            <span className={styles.tierDiscount}>
                                                                                {((1 - parseFloat(tier.price_per_millar) / parseFloat(price.price_per_millar)) * 100).toFixed(0)}% dto.
                                                                            </span>
                                                                            <div className={styles.tierActions}>
                                                                                <button
                                                                                    className={styles.tierEditBtn}
                                                                                    onClick={() => {
                                                                                        setEditingTierId(tier.id)
                                                                                        setEditTierForm({
                                                                                            min_millares: String(tier.min_millares),
                                                                                            price_per_millar: tier.price_per_millar,
                                                                                        })
                                                                                    }}
                                                                                >
                                                                                    <RiEditLine />
                                                                                </button>
                                                                                <button
                                                                                    className={styles.tierDeleteBtn}
                                                                                    onClick={() => {
                                                                                        deleteTier.mutate(tier.id, {
                                                                                            onSuccess: () => toast.success('Nivel eliminado'),
                                                                                            onError: () => toast.error('Error al eliminar'),
                                                                                        })
                                                                                    }}
                                                                                    disabled={deleteTier.isPending}
                                                                                >
                                                                                    <RiDeleteBinLine />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className={styles.tierEmpty}>Sin descuentos por volumen configurados</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )
                            )
                        )}
                    </tbody>
                </table>
            )}
        </div>
    )
}
